import { useState } from "react";
import { login, type AuthUser } from "../api";
import { Logo } from "./Logo";
import { ParticleField } from "./ParticleField";

interface Props {
  onSuccess: (user: AuthUser) => void;
}

export function Login({ onSuccess }: Props) {
  const [username, setUsername] = useState("njrc");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const user = await login(username, password);
      onSuccess(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dark-tile relative min-h-screen overflow-hidden bg-[color:var(--color-bg)]">
      {/* Layered police-siren gradient — red bloom from the LEFT, blue
          bloom from the RIGHT, meeting in a strobed wash behind the
          centered brand mark. The split makes the page feel like it's
          pulsing in emergency-vehicle light without being literally a
          police car interior. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background: [
            // Red bloom from bottom-left
            "radial-gradient(ellipse 60% 70% at 15% 95%, rgba(228, 0, 43, 0.55), transparent 65%)",
            // Blue bloom from bottom-right
            "radial-gradient(ellipse 60% 70% at 85% 95%, rgba(0, 70, 255, 0.55), transparent 65%)",
            // Soft red wash upper-left
            "radial-gradient(ellipse 55% 45% at 25% 25%, rgba(228, 0, 43, 0.25), transparent 70%)",
            // Soft blue wash upper-right
            "radial-gradient(ellipse 55% 45% at 75% 25%, rgba(0, 70, 255, 0.25), transparent 70%)",
            // Center darkening so the brand mark stays the focal point
            "radial-gradient(ellipse 70% 50% at 50% 45%, rgba(0,0,0,0.30), transparent 70%)",
            // Vignette frame
            "radial-gradient(ellipse 110% 80% at 50% 50%, transparent 50%, rgba(0,0,0,0.50) 100%)",
            // Base near-black backdrop
            "linear-gradient(180deg, #050608 0%, #0b0c14 50%, #0a050a 100%)",
          ].join(", "),
        }}
      />

      <ParticleField />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-sm fade-in">
          {/* Brand — the logo's wordmark is the wordmark; no need for redundant text.
              The brand block uses width: min(480, 90vw) so the logo can
              go BIGGER than the form's max-w-sm constraint without
              overflowing on phones — the column itself stays narrow for
              the password fields, but the logo above gets to breathe. */}
          <div className="mb-10 flex flex-col items-center text-center" style={{ width: "min(480px, 90vw)", marginInline: "auto" }}>
            <div
              style={{
                // Two strobe glows around the brand mark — red from one
                // side, blue from the other — to land the police-siren
                // theme without needing the logo itself to do all the
                // work. Gentle so it doesn't overpower.
                filter:
                  "drop-shadow(-14px 0 44px rgba(228, 0, 43, 0.55)) drop-shadow(14px 0 44px rgba(0, 70, 255, 0.55)) drop-shadow(0 6px 16px rgba(0,0,0,0.7))",
                width: "100%",
              }}
            >
              <Logo size={480} />
            </div>
            <p className="meta mt-5 text-[color:var(--color-text-dim)]">
              NJ municipal court records, served with judgment
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <label htmlFor="login-user" className="label mb-1.5 block">
                Username
              </label>
              <input
                id="login-user"
                autoComplete="username"
                className="input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div>
              <label htmlFor="login-pw" className="label mb-1.5 block">
                Password
              </label>
              <input
                id="login-pw"
                type="password"
                autoComplete="current-password"
                autoFocus
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />
            </div>

            {error && (
              <div className="rounded-lg border border-[color:var(--color-rose-hi)] bg-[color:var(--color-rose-soft)] px-3 py-2 text-sm text-[color:var(--color-rose-hi)]">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !password}
              className="btn btn-primary w-full"
              style={{ paddingBlock: 12 }}
            >
              {submitting ? "Signing in…" : "Enter"}
            </button>
          </form>

          <p className="mt-6 text-center text-[11px] text-[color:var(--color-text-dim)]">
            Your session is remembered on this device for 30 days.
          </p>
        </div>
      </div>
    </div>
  );
}
